/**
 * 全社管理者と拠点管理者で、管理画面に出すものを分ける（課題 #100）。
 *
 * 権限そのものは BE が守る（拠点管理者には 403 / 範囲外は 404）。ここは
 * 「押しても 403 になる操作を最初から出さない」ための表示の出し分けだけを受け持つ。
 * 判定できないとき（'unknown'）は従来どおりすべて出す。表示を減らさない側に倒しても、
 * BE が止めるので権限は越えない。
 *
 * 出どころ: BE PR #117（2026-09-26）・#129（2026-09-29）の「FE へ」
 *   - レポート・利用状況・監査ログは全社管理者のみ（拠点管理者は 403 REQUIRES_UNRESTRICTED_ADMIN）
 *   - 閲覧範囲の管理と通知先（Webhook）の設定も全社管理者のみ
 *   - 組織全体に効く設定（プロンプト・同意文書・システム設定・同意モード）も全社管理者のみ。
 *     部署マスタは見られるが、追加・変更・削除は全社管理者のみ（2026-09-29 高尾さん決定）
 *   - 拠点管理者が招待・編集で付けられる役割は従業員だけ
 *   - 範囲内でも、管理者・産業保健職のアカウントには編集・再設定・削除・書き出しができない
 */

/** 管理者の区分。'loading' は判定中、'unknown' は判定できなかった */
export type AdminScopeStatus = 'loading' | 'unrestricted' | 'restricted' | 'unknown';

/** 全社管理者だけが使える画面 */
export const UNRESTRICTED_ONLY_PATHS: readonly string[] = [
  '/admin/reports',
  '/admin/audit-logs',
  '/admin/admin-scopes',
  '/admin/webhooks',
  '/admin/prompts',
  '/admin/consent',
  '/admin/settings',
  '/admin/organization-mode',
];

/** 部署マスタを変更できるか。拠点管理者は見るだけ（BE PR #129） */
export function canEditDepartments(status: AdminScopeStatus): boolean {
  return status !== 'restricted';
}

/** この画面へのメニューを出すか。拠点管理者と分かっているときだけ、全社管理者専用の画面を隠す */
export function showAdminPath(path: string, status: AdminScopeStatus): boolean {
  return !(status === 'restricted' && UNRESTRICTED_ONLY_PATHS.includes(path));
}

/** 招待・編集で選べる役割。拠点管理者は従業員だけ */
export function assignableRoles<T extends string>(all: readonly T[], status: AdminScopeStatus): T[] {
  return status === 'restricted' ? all.filter((r) => r === 'employee') : [...all];
}

/**
 * 拠点管理者から操作できない行か（管理者・産業保健職のアカウント）。
 * BE は範囲内でもこれらへの編集・再設定・削除・書き出しを 403 で止める。
 */
export function isProtectedFromRestricted(role: string, status: AdminScopeStatus): boolean {
  return status === 'restricted' && role !== 'employee';
}

/** 全社管理者のみの操作で弾かれたか（BE の 403 REQUIRES_UNRESTRICTED_ADMIN） */
export const REQUIRES_UNRESTRICTED_ADMIN = 'REQUIRES_UNRESTRICTED_ADMIN';

/** ログイン中の管理者の閲覧範囲 */
/**
 * 利用者の削除を BE が止めているか（課題 #71。2026-10-07 高尾さん決定の案 2）。
 * stopped のときだけ「完全削除」を出さずに、止めていることを案内する（押してから断られる流れにしない）。
 * 項目を返さない BE では unknown とし、今の画面のままにする（BE が止めていれば、押した後に BE の断りが出る）
 */
export type UserDeletionStatus = 'enabled' | 'stopped' | 'unknown';

export interface AdminScopeInfo {
  status: AdminScopeStatus;
  /** 拠点管理者の閲覧範囲の部署。null は「分からない」（古い BE で判定した場合など） */
  departmentIds: string[] | null;
  departmentNames: string[] | null;
  /** 拠点管理者なのに範囲が 1 件も無い（何も見えない） */
  seesNothing: boolean;
  userDeletion: UserDeletionStatus;
}

export const UNKNOWN_SCOPE: AdminScopeInfo = {
  status: 'unknown',
  departmentIds: null,
  departmentNames: null,
  seesNothing: false,
  userDeletion: 'unknown',
};

/**
 * GET /v1/admin/admin-scopes/me の応答から作る（BE PR #124）。
 * all → 全社管理者 / departments → 拠点管理者。それ以外（想定外の形）は判定できないとし、
 * 表示を減らさない側に倒す（権限は BE が守る）。
 */
export function scopeInfoFromMe(item: unknown): AdminScopeInfo {
  const o = (item && typeof item === 'object' ? item : {}) as {
    admin_scope?: unknown;
    department_ids?: unknown;
    department_names?: unknown;
    sees_nothing?: unknown;
    user_deletion_enabled?: unknown;
  };
  const userDeletion: UserDeletionStatus =
    o.user_deletion_enabled === true ? 'enabled' : o.user_deletion_enabled === false ? 'stopped' : 'unknown';
  if (o.admin_scope === 'all') {
    return { status: 'unrestricted', departmentIds: [], departmentNames: [], seesNothing: false, userDeletion };
  }
  if (o.admin_scope === 'departments') {
    const ids = Array.isArray(o.department_ids) ? o.department_ids.filter((v): v is string => typeof v === 'string') : [];
    const names = Array.isArray(o.department_names)
      ? o.department_names.filter((v): v is string => typeof v === 'string')
      : [];
    return {
      status: 'restricted',
      departmentIds: ids,
      departmentNames: names,
      seesNothing: o.sees_nothing === true || ids.length === 0,
      userDeletion,
    };
  }
  return { ...UNKNOWN_SCOPE, userDeletion };
}

/**
 * 部署の選択肢。拠点管理者は閲覧範囲の部署だけ（BE は範囲外への変更を 403 で止める）。
 * 全社管理者・判定できないとき・範囲が分からないときは全部出す。
 */
export function departmentOptionsFor<T extends { id: string }>(departments: T[], info: AdminScopeInfo): T[] {
  if (info.status !== 'restricted' || info.departmentIds == null) return departments;
  const allowed = new Set(info.departmentIds);
  return departments.filter((d) => allowed.has(d.id));
}

/** 「所属なし」を選ばせるか。拠点管理者は所属なしへ移せない（BE が 403） */
export function allowNoDepartment(info: AdminScopeInfo): boolean {
  return info.status !== 'restricted';
}
